/**
 * Settings Validation Utilities
 * Validates self-sovereign mode configuration
 */

import type { FairdropSettings } from './index'

export interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
}

/**
 * Validate self-sovereign mode configuration
 */
export function validateSelfSovereignConfig(settings: FairdropSettings): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  if (!settings.selfSovereign) {
    return { valid: true, errors: [], warnings: [] }
  }

  // Required: Bee URL or gateways
  if (!settings.beeUrl && (!settings.swarmGateways || settings.swarmGateways.length === 0)) {
    errors.push('Self-sovereign mode requires a Bee node URL or Swarm gateways')
  }

  // Required: Stamp ID for uploads
  if (!settings.defaultStampId) {
    warnings.push('No default stamp ID configured - uploads will fail')
  }

  // Recommended: Ethereum RPC for ENS operations
  if (!settings.ethereumRpcUrl) {
    warnings.push('No Ethereum RPC URL configured - ENS operations will use public providers')
  }

  // Validate URL formats
  if (settings.beeUrl && !isValidUrl(settings.beeUrl)) {
    errors.push('Invalid Bee node URL format')
  }

  if (settings.ethereumRpcUrl && !isValidUrl(settings.ethereumRpcUrl)) {
    errors.push('Invalid Ethereum RPC URL format')
  }

  if (settings.swarmGateways) {
    for (const gateway of settings.swarmGateways) {
      if (!isValidUrl(gateway)) {
        errors.push(`Invalid Swarm gateway URL: ${gateway}`)
      }
    }
  }

  // Validate stamp ID format (64 hex chars)
  if (settings.defaultStampId && !isValidStampId(settings.defaultStampId)) {
    errors.push('Invalid stamp ID format (expected 64 hex characters)')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Test Bee node connection
 */
export async function testBeeConnection(url: string): Promise<{ success: boolean; error?: string }> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    })

    clearTimeout(timeout)

    if (!response.ok) {
      return { success: false, error: `HTTP ${response.status}` }
    }

    const text = await response.text()
    if (!text.includes('Ethereum Swarm Bee') && !text.includes('OK')) {
      return { success: false, error: 'Not a valid Bee node' }
    }

    return { success: true }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Connection timeout' }
      }
      return { success: false, error: error.message }
    }
    return { success: false, error: 'Connection failed' }
  }
}

/**
 * Test Ethereum RPC connection
 */
export async function testEthereumRpc(url: string): Promise<{ success: boolean; error?: string; chainId?: number }> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_chainId',
        params: [],
        id: 1,
      }),
      signal: controller.signal,
    })

    clearTimeout(timeout)

    if (!response.ok) {
      return { success: false, error: `HTTP ${response.status}` }
    }

    const data = await response.json() as { result?: string; error?: { message: string } }

    if (data.error) {
      return { success: false, error: data.error.message }
    }

    if (!data.result) {
      return { success: false, error: 'Invalid RPC response' }
    }

    const chainId = parseInt(data.result, 16)
    return { success: true, chainId }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Connection timeout' }
      }
      return { success: false, error: error.message }
    }
    return { success: false, error: 'Connection failed' }
  }
}

/**
 * Test stamp validity by checking Bee node
 */
export async function testStampValidity(
  beeUrl: string,
  stampId: string
): Promise<{ success: boolean; error?: string; usable?: boolean; expiresAt?: number }> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(`${beeUrl}/stamps/${stampId}`, {
      method: 'GET',
      signal: controller.signal,
    })

    clearTimeout(timeout)

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'Stamp not found' }
      }
      return { success: false, error: `HTTP ${response.status}` }
    }

    const data = await response.json() as { usable?: boolean; batchTTL?: number }

    const expiresAt = data.batchTTL ? Date.now() + data.batchTTL * 1000 : undefined

    return {
      success: true,
      usable: data.usable ?? false,
      expiresAt,
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Connection timeout' }
      }
      return { success: false, error: error.message }
    }
    return { success: false, error: 'Connection failed' }
  }
}

// Helpers

function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

function isValidStampId(stampId: string): boolean {
  return /^[a-fA-F0-9]{64}$/.test(stampId)
}
